import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import PanelTemplate from "../../../templates/Panel";
import PageHeader from "../../../components/PageHeader";
import Box from "../../../components/Box";
import Table from "../../../components/Table";
import Button from "../../../components/Button";
import Pagination from "../../../components/Pagination";
import FilterSimple from "../../../components/FilterSimple";
import { ModalDelete } from "../../../components/ModalDelete";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { hasPermission } from "../../../services/auth";
import { formatCfop, getDirectionLabel, getPurposeLabel } from "../helpers";

const searchParams = [
    { name: 'Nome', key: '[name]', operation: 'contain', type: 'text' },
];

const normalizePage = page => {
    const parsed = parseInt(page, 10);
    return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
};

// O estado da listagem mora na query string (padrão das listas do sistema):
// o filtro sobrevive ao voltar da edição e o link pode ser compartilhado.
const parseFilters = search => {
    const params = new URLSearchParams(search);

    return {
        page: normalizePage(params.get('page')),
        search: params.get('search') || '',
        direction: params.get('direction') || '',
    };
};

const buildApiQuery = filters => {
    const params = new URLSearchParams();

    if (filters.search.trim() !== '') params.set('search', filters.search.trim());
    if (filters.direction !== '') params.set('direction', filters.direction);
    params.set('page', filters.page);

    return params.toString();
};

const buildListSearch = filters => {
    const params = new URLSearchParams();

    if (filters.page > 1) params.set('page', filters.page);
    if (filters.search.trim() !== '') params.set('search', filters.search.trim());
    if (filters.direction !== '') params.set('direction', filters.direction);

    return params.toString();
};

/**
 * Naturezas de operação (BDM-887).
 *
 * A natureza diz o que a nota está fazendo e como é tributada. A coluna de
 * situação tributária mostra os dois códigos porque o que vale depende do
 * regime da empresa que emite: CSOSN no Simples, CST fora dele.
 */
export const FiscalOperationList = () => {
    const [loading, setLoading] = useState(true);
    const [operations, setOperations] = useState([]);
    const [data, setData] = useState({ header: [], rows: [] });
    const [operationToDelete, setOperationToDelete] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const filters = useMemo(() => parseFilters(location.search), [location.search]);
    const canManage = hasPermission('manage_fiscal');

    useEffect(() => {
        getOperations();
    }, [location.search]);

    useEffect(() => {
        generateData();
    }, [operations]);

    const updateUrl = (changes, resetPage = true) => {
        const next = {
            ...filters,
            ...changes,
            page: resetPage ? 1 : (changes.page || filters.page),
        };
        const search = buildListSearch(next);

        navigate({ pathname: location.pathname, search: search ? `?${search}` : '' });
    };

    const getOperations = () => {
        setLoading(true);

        api.get(`/fiscal-operation/paginate?${buildApiQuery(filters)}`).then(res => {
            const resources = res.data.resources;

            setOperations(resources.data);
            setCurrentPage(resources.current_page);
            setLastPage(resources.last_page);
            setPerPage(resources.per_page);
            setTotalResults(resources.total);
            setHasNextPage(resources.next_page_url !== null);
            setHasPrevPage(resources.prev_page_url !== null);
        }).catch(renderError).then(() => setLoading(false));
    };

    const baseCst = (operation, tax) =>
        operation.rules?.find(rule => rule.tax === tax && !rule.is_exception)?.cst ?? '—';

    const exceptionCount = operation =>
        (operation.rules ?? []).filter(rule => rule.is_exception).length;

    const generateData = () => {
        const header = ['Natureza', 'Tipo', 'Finalidade', 'CFOP', 'CSOSN / CST', 'Exceções'];

        if (canManage) header.push('');

        const returnTo = encodeURIComponent(`/fiscal/operations${location.search}`);

        const rows = operations.map(operation => {
            const buttons = [];

            if (canManage) {
                buttons.push(
                    <Button type={`secondary`} svg={`copy`} action={() => duplicate(operation.id)} />
                );

                // A padrão não pode ser excluída: a emissão ficaria sem CFOP
                // nem tributação. O caminho é eleger outra antes.
                if (!operation.is_default) {
                    buttons.push(
                        <Button type={`delete`} svg={`delete`} action={() => setOperationToDelete(operation.id)} />
                    );
                }
            }

            const exceptions = exceptionCount(operation);

            return {
                data: [
                    <div className="operation-name">
                        <strong>{operation.name}</strong>
                        {operation.is_default && <span className="tag-default">Padrão</span>}
                        {!operation.active && <span className="tag-inactive">Inativa</span>}
                    </div>,
                    getDirectionLabel(operation.direction),
                    getPurposeLabel(operation.purpose),
                    `${formatCfop(operation.cfop_internal) || '—'} / ${formatCfop(operation.cfop_interstate) || '—'}`,
                    `${baseCst(operation, 'simples')} / ${baseCst(operation, 'icms')}`,
                    exceptions > 0 ? `${exceptions}` : '—',
                ],
                to: `/fiscal/operations/${operation.id}?return_to=${returnTo}`,
                buttons,
                id: operation.id,
            };
        });

        setData({ header, rows });
    };

    const duplicate = id => {
        setLoading(true);

        api.post(`/fiscal-operation/${id}/duplicate`).then(res => {
            toast.success('Natureza duplicada');
            navigate(`/fiscal/operations/${res.data.resource.id}`);
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    };

    const deleteOperation = () => {
        setLoading(true);

        api.delete(`/fiscal-operation/${operationToDelete}`).then(() => {
            setOperationToDelete(null);
            getOperations();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    };

    return (
        <>
            <ModalDelete
                show={operationToDelete !== null}
                text={<p>Tem certeza que deseja apagar esta natureza de operação? As notas já emitidas não mudam.</p>}
                action={deleteOperation}
                loading={loading}
                close={() => setOperationToDelete(null)}
            />
            <PanelTemplate id="fiscal-operation-list">
                <PageHeader
                    left={
                        <FilterSimple
                            params={searchParams}
                            filter={(value, metadata) => updateUrl({ search: metadata?.value ?? '' })}
                            externalValue={filters.search}
                            externalOption={searchParams[0].key}
                        />
                    }
                    right={
                        <>
                            <div className="direction-filter">
                                <button
                                    className={filters.direction === '' ? 'active' : ''}
                                    onClick={() => updateUrl({ direction: '' })}
                                >Todas</button>
                                <button
                                    className={filters.direction === 'out' ? 'active' : ''}
                                    onClick={() => updateUrl({ direction: 'out' })}
                                >Saída</button>
                                <button
                                    className={filters.direction === 'in' ? 'active' : ''}
                                    onClick={() => updateUrl({ direction: 'in' })}
                                >Entrada</button>
                            </div>
                            {/* Esta tela saiu do menu: sem um caminho de volta
                                explícito, só o botão do navegador levaria de
                                volta às configurações. */}
                            <Button
                                type={`secondary`}
                                size={`small`}
                                svg={`config`}
                                text={`Configurações fiscais`}
                                link={`/fiscal/settings`}
                            />
                            {canManage &&
                                <Button
                                    type={`primary`}
                                    size={`small`}
                                    svg={`plus-white`}
                                    text={`Cadastrar natureza`}
                                    action={() => navigate('/fiscal/operations/new')}
                                />
                            }
                        </>
                    }
                />

                <Box className={`no-padding with-pagination`}>
                    <Table header={data.header} rows={data.rows} loading={loading} />
                </Box>

                {!loading &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={page => updateUrl({ page }, false)}
                    />
                }
            </PanelTemplate>
        </>
    );
};

export default FiscalOperationList;
